const API = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
const SESSION_KEY = 'sa_access_token';
let token: string | null = sessionStorage.getItem(SESSION_KEY);

export function getToken(): string | null { return token; }
export function setToken(t: string) { token = t; sessionStorage.setItem(SESSION_KEY, t); }
export function setRefreshToken(_t: string) { /* refresh token managed via HttpOnly cookie */ }
export async function silentRefresh(): Promise<boolean> {
  try {
    const r = await fetch(`${API}/superadmin/refresh`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
    if (!r.ok) return false;
    const data = await r.json() as { data: { access_token: string } };
    setToken(data.data.access_token);
    return true;
  } catch { return false; }
}
export function loadToken() { /* no-op: token loaded from sessionStorage at module init */ }
export function clearToken() {
  token = null;
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem('sa_token'); localStorage.removeItem('sa_refresh_token'); localStorage.removeItem('sa_user');
}

async function api<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> ?? {}),
  };
  if (token) h['Authorization'] = `Bearer ${token}`;
  const r = await fetch(`${API}${path}`, { ...opts, headers: h, credentials: 'include' });
  if (r.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed && token) {
      h['Authorization'] = `Bearer ${token}`;
      const retryRes = await fetch(`${API}${path}`, { ...opts, headers: h, credentials: 'include' });
      if (!retryRes.ok) { const e = await retryRes.json().catch(() => ({})) as { error?: { message?: string } }; throw new Error(e.error?.message ?? `HTTP ${retryRes.status}`); }
      return retryRes.json() as Promise<T>;
    }
    clearToken(); window.location.replace('/login');
    throw new Error('認証が必要です。再ログインしてください。');
  }
  if (!r.ok) {
    const e = await r.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(e.error?.message ?? `HTTP ${r.status}`);
  }
  return r.json() as Promise<T>;
}

// ── 認証 ──────────────────────────────────────────────────────────────────
export interface SaUser { id: string; name: string; email: string; sa_role: 'owner' | 'admin'; }

export async function login(email: string, password: string): Promise<
  { mfa_required: true; mfa_token: string } |
  { access_token: string; refresh_token: string; user: SaUser }
> {
  const r = await fetch(`${API}/superadmin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(e.error?.message ?? `HTTP ${r.status}`);
  }
  const res = await r.json() as { data: { mfa_required?: boolean; mfa_token?: string; access_token?: string; refresh_token?: string; user?: SaUser } };
  if (res.data.mfa_required) {
    return { mfa_required: true, mfa_token: res.data.mfa_token! };
  }
  if (res.data.refresh_token) setRefreshToken(res.data.refresh_token);
  return { access_token: res.data.access_token!, refresh_token: res.data.refresh_token!, user: res.data.user! };
}

export async function mfaChallenge(mfaToken: string, code: string, trustIp: boolean) {
  const r = await fetch(`${API}/superadmin/mfa/challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mfa_token: mfaToken, code, trust_ip: trustIp }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(e.error?.message ?? `HTTP ${r.status}`);
  }
  const res = await r.json() as { data: { access_token: string; refresh_token: string; user: SaUser } };
  if (res.data.refresh_token) setRefreshToken(res.data.refresh_token);
  return res.data;
}

export async function mfaSetup() {
  return api<{ data: { secret: string; qrcode: string } }>('/superadmin/mfa/setup', { method: 'POST' }).then(r => r.data);
}

export async function mfaEnable(code: string) {
  return api<{ data: { message: string } }>('/superadmin/mfa/enable', { method: 'POST', body: JSON.stringify({ code }) }).then(r => r.data);
}

export async function mfaDisable(code: string) {
  return api<{ data: { message: string } }>('/superadmin/mfa/disable', { method: 'POST', body: JSON.stringify({ code }) }).then(r => r.data);
}

export async function resetMfa(targetAdminId: string) {
  return api<{ data: { id: string } }>(`/superadmin/mfa/${targetAdminId}`, { method: 'DELETE' }).then(r => r.data);
}

// ── 会社管理 ──────────────────────────────────────────────────────────────
export interface Company {
  id: string; name: string; subdomain: string; created_at: string;
  is_active: number; user_count: number; vessel_count: number; last_activity: string | null;
  memo: string | null; sales_contact: string | null;
  contract_date: string | null; renewal_date: string | null;
}

export interface CompanyDetail {
  id: string; name: string; subdomain: string; created_at: string; is_active: number;
  memo: string | null; sales_contact: string | null;
  contract_date: string | null; renewal_date: string | null;
  website_url: string | null;
  logo_url: string | null;
  admins: { id: string; employee_code: string; user_name: string; email: string; user_role: string; is_active: number; last_login_at: string | null; mfa_enabled?: number }[];
}

/** 会社ロゴのアップロード（管理サイト・船員アプリのログイン画面/ヘッダーに表示） */
export async function uploadCompanyLogo(companyId: string, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('logo', file);
  const res = await fetch(`${API}/superadmin/companies/${companyId}/logo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken() ?? ''}` },
    body: fd,
    credentials: 'include',
  });
  if (!res.ok) throw new Error('ロゴのアップロードに失敗しました');
  const json = await res.json();
  return json.data.logo_url as string;
}

/** 会社ロゴの削除 */
export async function deleteCompanyLogo(companyId: string): Promise<void> {
  const res = await fetch(`${API}/superadmin/companies/${companyId}/logo`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken() ?? ''}` },
    credentials: 'include',
  });
  if (!res.ok) throw new Error('ロゴの削除に失敗しました');
}

export async function listCompanies(): Promise<Company[]> {
  const res = await api<{ data: Company[] }>('/superadmin/companies');
  return res.data;
}

export async function createCompany(name: string, subdomain: string): Promise<{ id: string; name: string; subdomain: string }> {
  const res = await api<{ data: { id: string; name: string; subdomain: string } }>(
    '/superadmin/companies', { method: 'POST', body: JSON.stringify({ name, subdomain }) }
  );
  return res.data;
}

export async function getCompany(id: string): Promise<CompanyDetail> {
  const res = await api<{ data: CompanyDetail }>(`/superadmin/companies/${id}`);
  return res.data;
}

// ── 会社別 船員APK ビルド ──
export interface ApkBuildStatus { status: 'idle' | 'running' | 'success' | 'error'; log: string[]; apkUrl?: string; startedAt?: number; finishedAt?: number; }
export async function buildCompanyApk(companyId: string): Promise<{ started: boolean; subdomain: string }> {
  const res = await api<{ data: { started: boolean; subdomain: string } }>(`/mdm/sa/build-apk`, { method: 'POST', body: JSON.stringify({ company_id: companyId }) });
  return res.data;
}
export async function getApkBuildStatus(companyId: string): Promise<ApkBuildStatus> {
  const res = await api<{ data: ApkBuildStatus }>(`/mdm/sa/build-apk/${companyId}`);
  return res.data;
}

export async function updateCompany(id: string, name: string, subdomain: string): Promise<{ id: string; name: string; subdomain: string }> {
  const res = await api<{ data: { id: string; name: string; subdomain: string } }>(
    `/superadmin/companies/${id}`, { method: 'PUT', body: JSON.stringify({ name, subdomain }) }
  );
  return res.data;
}

export async function updateCompanyMeta(id: string, meta: { memo?: string; sales_contact?: string; contract_date?: string; renewal_date?: string; website_url?: string }): Promise<void> {
  await api(`/superadmin/companies/${id}/meta`, { method: 'PATCH', body: JSON.stringify(meta) });
}

// ── 機能フラグ（新機能を会社ごとに先行公開する） ──
export interface FeatureFlagDef { key: string; label: string; description: string; noRollout?: boolean; category?: string }
export interface FeatureFlagsData {
  defined: FeatureFlagDef[];
  /** 種類の並び順（サーバー定義） */
  categories?: string[];
  companies: { id: string; name: string; subdomain: string | null; flags: Record<string, boolean> }[];
}

/** 定義済みフラグと各社の設定状況 */
export async function getFeatureFlags(): Promise<FeatureFlagsData> {
  const res = await api<{ data: FeatureFlagsData }>('/superadmin/feature-flags');
  return res.data;
}

/** 1社のフラグをON/OFF（null で既定に戻す） */
export async function setCompanyFeatureFlag(companyId: string, key: string, value: boolean | null): Promise<Record<string, boolean>> {
  const res = await api<{ data: Record<string, boolean> }>(
    `/superadmin/companies/${companyId}/feature-flags`, { method: 'PATCH', body: JSON.stringify({ key, value }) }
  );
  return res.data;
}

/** フラグを全社へ展開 */
export async function rolloutFeatureFlag(key: string, value = true): Promise<{ key: string; value: boolean; companies: number }> {
  const res = await api<{ data: { key: string; value: boolean; companies: number } }>(
    `/superadmin/feature-flags/${encodeURIComponent(key)}/rollout`, { method: 'POST', body: JSON.stringify({ value }) }
  );
  return res.data;
}

export async function toggleCompanyStatus(id: string): Promise<{ id: string; is_active: number }> {
  const res = await api<{ data: { id: string; is_active: number } }>(
    `/superadmin/companies/${id}/status`, { method: 'PATCH' }
  );
  return res.data;
}

export async function deleteCompany(id: string): Promise<void> {
  await api(`/superadmin/companies/${id}`, { method: 'DELETE' });
}

// ── 管理者ユーザー ────────────────────────────────────────────────────────
export async function createAdminUser(companyId: string, data: { user_name: string; employee_code: string; email: string; password: string }): Promise<{ id: string }> {
  const res = await api<{ data: { id: string } }>(
    `/superadmin/companies/${companyId}/admin-users`, { method: 'POST', body: JSON.stringify(data) }
  );
  return res.data;
}

export async function toggleAdminUser(companyId: string, userId: string): Promise<{ id: string; is_active: number }> {
  const res = await api<{ data: { id: string; is_active: number } }>(
    `/superadmin/companies/${companyId}/admin-users/${userId}/toggle`, { method: 'PATCH' }
  );
  return res.data;
}

export async function resetAdminPassword(companyId: string, userId: string, newPassword: string): Promise<void> {
  await api(`/superadmin/companies/${companyId}/admin-users/${userId}/password`, {
    method: 'PATCH', body: JSON.stringify({ new_password: newPassword }),
  });
}
export async function resetAdminMfa(companyId: string, userId: string): Promise<void> {
  await api(`/superadmin/companies/${companyId}/admin-users/${userId}/reset-mfa`, { method: 'POST' });
}

// ── ユーザー完全削除 ──────────────────────────────────────────────────────
export interface CompanyUser {
  id: string; employee_code: string; user_name: string; user_role: string;
  is_active: number; last_login_at: string | null; created_at: string;
}

export async function listCompanyUsers(companyId: string): Promise<CompanyUser[]> {
  const res = await api<{ data: CompanyUser[] }>(`/superadmin/companies/${companyId}/users`);
  return res.data;
}

export async function purgeUser(companyId: string, userId: string): Promise<void> {
  await api(`/superadmin/companies/${companyId}/users/${userId}`, { method: 'DELETE' });
}

// ── 運営管理者管理（オーナーのみ） ───────────────────────────────────
export interface SuperAdmin { id: string; name: string; email: string; sa_role: string; is_active: number; last_login_at: string | null; created_at: string; }

export async function listSuperAdmins(): Promise<SuperAdmin[]> {
  const res = await api<{ data: SuperAdmin[] }>('/superadmin/super-admins');
  return res.data;
}

export async function createSuperAdmin(data: { name: string; email: string; password: string; sa_role: string }): Promise<SuperAdmin> {
  const res = await api<{ data: SuperAdmin }>('/superadmin/super-admins', { method: 'POST', body: JSON.stringify(data) });
  return res.data;
}

export async function toggleSuperAdmin(saId: string): Promise<{ id: string; is_active: number }> {
  const res = await api<{ data: { id: string; is_active: number } }>(
    `/superadmin/super-admins/${saId}/toggle`, { method: 'PATCH' }
  );
  return res.data;
}

export async function deleteSuperAdmin(saId: string): Promise<void> {
  await api(`/superadmin/super-admins/${saId}`, { method: 'DELETE' });
}

// ── 運営管理者の会社アクセス権（オーナー管理） ───────────────────────
export interface CompanyAccess { all_access: boolean; company_ids: string[]; }

/** 対象運営管理者のアクセス許可会社を取得（オーナーのみ） */
export async function getCompanyAccess(saId: string): Promise<CompanyAccess> {
  const res = await api<{ data: CompanyAccess }>(`/superadmin/super-admins/${saId}/company-access`);
  return res.data;
}

/** 対象運営管理者のアクセス許可会社を一括更新（オーナーのみ） */
export async function setCompanyAccess(saId: string, companyIds: string[]): Promise<{ company_ids: string[] }> {
  const res = await api<{ data: { company_ids: string[] } }>(
    `/superadmin/super-admins/${saId}/company-access`,
    { method: 'PUT', body: JSON.stringify({ company_ids: companyIds }) }
  );
  return res.data;
}

/** 自分がアクセス可能な会社（owner は all_access=true） */
export async function getMyCompanyAccess(): Promise<CompanyAccess> {
  const res = await api<{ data: CompanyAccess }>('/superadmin/my-company-access');
  return res.data;
}

/** 会社を運営管理者として開くための管理コンソール用トークンを発行 */
export async function accessCompanyAsSuperAdmin(companyId: string): Promise<{ access_token: string; subdomain: string; company_name: string }> {
  const res = await api<{ data: { access_token: string; subdomain: string; company_name: string } }>(
    `/superadmin/companies/${companyId}/access`, { method: 'POST' }
  );
  return res.data;
}

/**
 * 運営管理者として対象会社の管理コンソールを新規タブで開く。
 * 発行トークンを URL フラグメント(#sa=) で渡し、管理コンソール側が取り込む。
 */
export function openCompanyCrewApp(subdomain: string, accessToken: string): void {
  const host = window.location.hostname;
  const baseDomain = host.split('.').slice(1).join('.') || 'unison-sea.com';
  // 会社ごとの船員アプリ（<会社>.crew.<ドメイン>）を管理端末モードで開く。
  // 運営アカウントは打刻できない（確認用）。アプリ側で止めている。
  const url = `${window.location.protocol}//${subdomain}.crew.${baseDomain}/?app=managed#sa=${encodeURIComponent(accessToken)}`;
  window.open(url, '_blank', 'noopener');
}

export function openCompanyAdminConsole(subdomain: string, accessToken: string): void {
  const host = window.location.hostname;
  // superadmin.unison-sea.com → unison-sea.com
  const baseDomain = host.split('.').slice(1).join('.') || 'unison-sea.com';
  const url = `${window.location.protocol}//${subdomain}.${baseDomain}/#sa=${encodeURIComponent(accessToken)}`;
  window.open(url, '_blank', 'noopener');
}

// ── 自分のパスワード変更 ──────────────────────────────────────────────────
export async function changeSuperAdminPassword(currentPassword: string, newPassword: string): Promise<void> {
  await api('/superadmin/profile/password', {
    method: 'PATCH', body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

// ── 活動統計 ──────────────────────────────────────────────────────────────
export interface DailyCount { date: string; count: number; }
export interface CompanyStat { company_id: string; company_name: string; count: number; }

export async function getGlobalActivity(days = 30): Promise<{ daily: DailyCount[]; by_company: CompanyStat[] }> {
  const res = await api<{ data: { daily: DailyCount[]; by_company: CompanyStat[] } }>(`/superadmin/stats/activity?days=${days}`);
  return res.data;
}

export async function getCompanyActivity(companyId: string, days = 30): Promise<DailyCount[]> {
  const res = await api<{ data: DailyCount[] }>(`/superadmin/companies/${companyId}/activity?days=${days}`);
  return res.data;
}

export interface CompanyAuditLog {
  id: string; user_id: string | null; user_name: string | null; user_role: string | null; app_type: string | null;
  method: string; path: string; action: string; status: number | null; summary: string | null; ip: string | null; created_at: string;
}
/** 会社の操作履歴（誰が・いつ・何をしたか）。障害の一次切り分け用 */
export async function getCompanyAuditLogs(companyId: string, opts: { limit?: number; q?: string; from?: string; to?: string } = {}): Promise<CompanyAuditLog[]> {
  const qs = new URLSearchParams();
  qs.set('limit', String(opts.limit ?? 100));
  if (opts.q) qs.set('q', opts.q);
  if (opts.from) qs.set('from', opts.from);
  if (opts.to) qs.set('to', opts.to);
  const res = await api<{ data: CompanyAuditLog[] }>(`/superadmin/companies/${companyId}/audit-logs?${qs}`);
  return res.data;
}

// ── 会社設定 ──────────────────────────────────────────────────────────────
export interface CompanySettings { alcohol_threshold: number; overtime_hours: number; work_hours_limit: number; }

export async function getCompanySettings(companyId: string): Promise<CompanySettings> {
  const res = await api<{ data: CompanySettings }>(`/superadmin/companies/${companyId}/settings`);
  return res.data;
}

export async function updateCompanySettings(companyId: string, settings: Partial<CompanySettings>): Promise<void> {
  await api(`/superadmin/companies/${companyId}/settings`, { method: 'PUT', body: JSON.stringify(settings) });
}

// ── プロフィール ──────────────────────────────────────────────────────────
export async function getProfile(): Promise<SaUser> {
  const res = await api<{ data: SaUser }>('/superadmin/profile');
  return res.data;
}

export async function updateProfile(data: { name?: string; email?: string }): Promise<SaUser> {
  const res = await api<{ data: SaUser }>('/superadmin/profile', { method: 'PATCH', body: JSON.stringify(data) });
  return res.data;
}

// ── 一括メッセージ ────────────────────────────────────────────────────────
export async function sendBroadcast(companyIds: string[] | 'all', content: string, messageType = 'announcement'): Promise<{ sent_to: number }> {
  const res = await api<{ data: { sent_to: number } }>('/superadmin/broadcast', {
    method: 'POST', body: JSON.stringify({ company_ids: companyIds, content, message_type: messageType }),
  });
  return res.data;
}

// ── 操作ログ ──────────────────────────────────────────────────────────────
export interface AuditLog {
  id: string; actor_id: string; actor_name: string; actor_role: string;
  company_id: string | null; company_name: string | null;
  action: string; target_type: string | null; target_id: string | null; target_name: string | null;
  detail: string | null; ip_address: string | null; created_at: string;
}

export interface AuditLogFilter {
  action?: string; company_id?: string; actor?: string; date_from?: string; date_to?: string;
}

function auditLogParams(params?: AuditLogFilter): URLSearchParams {
  const q = new URLSearchParams();
  if (params?.action)     q.set('action', params.action);
  if (params?.company_id) q.set('company_id', params.company_id);
  if (params?.actor)      q.set('actor', params.actor);
  if (params?.date_from)  q.set('date_from', params.date_from);
  if (params?.date_to)    q.set('date_to', params.date_to);
  return q;
}

// ── 個人スマホアプリのアクセスログ ──────────────────────────────
export interface PersonalLog {
  id: string; company_id: string; company_name: string | null;
  user_id: string; user_name: string | null; employee_code: string | null;
  event: string; detail: string | null; ip_address: string | null;
  user_agent: string | null; created_at: string;
}

export async function getPersonalLogs(params?: { company_id?: string; event?: string; date_from?: string; date_to?: string; limit?: number; offset?: number }): Promise<{ data: PersonalLog[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.company_id) q.set('company_id', params.company_id);
  if (params?.event)      q.set('event', params.event);
  if (params?.date_from)  q.set('date_from', params.date_from);
  if (params?.date_to)    q.set('date_to', params.date_to);
  if (params?.limit)      q.set('limit', String(params.limit));
  if (params?.offset)     q.set('offset', String(params.offset));
  return api<{ data: PersonalLog[]; total: number }>(`/superadmin/personal-logs?${q.toString()}`);
}

export async function getAuditLogs(params?: AuditLogFilter & { limit?: number; offset?: number }): Promise<{ data: AuditLog[]; total: number }> {
  const q = auditLogParams(params);
  if (params?.limit)  q.set('limit',  String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  const res = await api<{ data: AuditLog[]; total: number }>(`/superadmin/audit-logs?${q.toString()}`);
  return res;
}

export async function exportAuditLogs(params?: AuditLogFilter): Promise<void> {
  const q = auditLogParams(params);
  const h: Record<string, string> = {};
  if (token) h['Authorization'] = `Bearer ${token}`;
  let res = await fetch(`${API}/superadmin/audit-logs/export?${q.toString()}`, { headers: h, credentials: 'include' });
  if (res.status === 401) {
    const refreshed = await silentRefresh();
    if (refreshed && token) {
      h['Authorization'] = `Bearer ${token}`;
      res = await fetch(`${API}/superadmin/audit-logs/export?${q.toString()}`, { headers: h, credentials: 'include' });
    } else {
      clearToken(); window.location.replace('/login');
      throw new Error('認証が必要です。再ログインしてください。');
    }
  }
  if (!res.ok) {
    const e = await res.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(e.error?.message ?? `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-logs_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── 顧客対応（問い合わせ）──────────────────────────────────────────────────
export interface SupportThread {
  id: string; company_id: string; company_name: string | null;
  created_by_name: string | null; subject: string; status: 'open' | 'closed';
  unread_count: number; last_message: string | null;
  created_at: string; updated_at: string;
}
export interface SupportMessage {
  id: string; thread_id: string; sender_type: 'admin' | 'super' | 'system';
  sender_name: string | null; body: string; created_at: string;
  attachment_path?: string | null; attachment_name?: string | null; attachment_mime?: string | null;
}

export async function listSupportThreads(): Promise<SupportThread[]> {
  const res = await api<{ data: SupportThread[] }>('/superadmin/support/threads');
  return res.data;
}

export async function getSupportUnreadCount(): Promise<number> {
  const res = await api<{ data: { count: number } }>('/superadmin/support/unread-count');
  return res.data.count;
}

export async function getSupportMessages(threadId: string): Promise<{ thread: SupportThread; messages: SupportMessage[]; peer_typing: boolean }> {
  const res = await api<{ data: { thread: SupportThread; messages: SupportMessage[]; peer_typing: boolean } }>(`/superadmin/support/threads/${threadId}/messages`);
  return res.data;
}

export async function postSupportTyping(threadId: string): Promise<void> {
  await api(`/superadmin/support/threads/${threadId}/typing`, { method: 'POST', body: JSON.stringify({}) });
}

export async function uploadSupportAttachment(threadId: string, file: File): Promise<void> {
  const fd = new FormData();
  fd.append('file', file);
  const h: Record<string, string> = {};
  if (token) h['Authorization'] = `Bearer ${token}`;
  const r = await fetch(`${API}/superadmin/support/threads/${threadId}/attachments`, {
    method: 'POST', headers: h, body: fd, credentials: 'include',
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(e.error?.message ?? `HTTP ${r.status}`);
  }
}

export async function fetchSupportAttachment(messageId: string): Promise<Blob> {
  const h: Record<string, string> = {};
  if (token) h['Authorization'] = `Bearer ${token}`;
  const r = await fetch(`${API}/superadmin/support/attachments/${messageId}`, { headers: h, credentials: 'include' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.blob();
}

export async function postSupportMessage(threadId: string, body: string): Promise<SupportMessage> {
  const res = await api<{ data: SupportMessage }>(`/superadmin/support/threads/${threadId}/messages`, {
    method: 'POST', body: JSON.stringify({ body }),
  });
  return res.data;
}

export async function setSupportThreadStatus(threadId: string, status: 'open' | 'closed'): Promise<void> {
  await api(`/superadmin/support/threads/${threadId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
}

// ── 資材（貸与端末）管理 ──────────────────────────────────────────────────
export interface AssetUsage {
  kind: 'vessel_device' | 'mdm';
  device_name?: string | null; vessel_name?: string | null; last_seen?: string | null; is_active?: number;
  mdm_model?: string | null; applied_mode?: string | null;
}
export interface Asset {
  id: string; asset_no: string; asset_type: 'tablet' | 'smartphone' | 'pc' | 'other';
  model: string | null; serial_no: string | null;
  status: 'in_stock' | 'lent' | 'repair' | 'retired';
  company_id: string | null; company_name: string | null;
  lent_at: string | null; note: string | null;
  tenant_device_id: string | null; mdm_device_id: string | null;
  usage: AssetUsage | null;
  created_at: string; updated_at: string;
}
export interface CompanyDeviceOption {
  id: string; device_name: string | null; vessel_name: string | null; last_seen: string | null; is_active: number;
}

export async function listAssets(companyId?: string): Promise<Asset[]> {
  const q = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
  const res = await api<{ data: Asset[] }>(`/superadmin/assets${q}`);
  return res.data;
}

export async function createAsset(input: { asset_no: string; asset_type: string; model?: string; serial_no?: string; note?: string }): Promise<{ id: string }> {
  const res = await api<{ data: { id: string } }>('/superadmin/assets', { method: 'POST', body: JSON.stringify(input) });
  return res.data;
}

export async function updateAsset(id: string, patch: Record<string, string | null>): Promise<void> {
  await api(`/superadmin/assets/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
}

export async function deleteAsset(id: string): Promise<void> {
  await api(`/superadmin/assets/${id}`, { method: 'DELETE' });
}

export async function listCompanyDevices(companyId: string): Promise<CompanyDeviceOption[]> {
  const res = await api<{ data: CompanyDeviceOption[] }>(`/superadmin/assets/company-devices/${companyId}`);
  return res.data;
}

// ── MDM 端末（キオスク）管理 ──────────────────────────────────────────────
export interface MdmDevice {
  device_id: string; model: string; is_device_owner: number;
  applied_mode: string; desired_mode: string; whitelist: string[];
  note: string; label: string; phone_number: string;
  company_id: string | null; company_name: string | null;
  online: number; last_seen: string;
}

export async function listMdmDevices(): Promise<MdmDevice[]> {
  const res = await api<{ data: MdmDevice[] }>('/mdm/sa/devices');
  return res.data;
}

/**
 * 船に置いてある共有管理端末（打刻用タブレット）。
 * 全社横断で見る目的は、充電残量とアルコール検知器の使用回数の把握。
 * 電池切れだと打刻ができず、検知器は寿命1000回の使い捨てで交換品の手配が要る。
 */
export interface SharedTerminal {
  id: string;
  company_id: string;
  company_name: string;
  vessel_name: string | null;
  device_name: string | null;
  phone_number: string | null;
  management_number: string | null;
  last_seen: string | null;
  last_battery: number | null;
  last_battery_charging: number | null;
  last_battery_at: string | null;
  /** アルコール検知器の使用回数。端末に検知器の記録が無ければ null */
  alcohol_count: number | null;
  alcohol_max_count: number | null;
  alcohol_last_measured_at: string | null;
}

export async function listSharedTerminals(): Promise<SharedTerminal[]> {
  const res = await api<{ data: SharedTerminal[] }>('/superadmin/shared-terminals');
  return res.data;
}

export type MdmMode = 'locked' | 'relaxed' | 'open';

export async function updateMdmDevice(
  deviceId: string,
  patch: { mode?: MdmMode; note?: string; label?: string; phone_number?: string; company_id?: string | null; whitelist?: string[] },
): Promise<void> {
  await api(`/mdm/sa/devices/${deviceId}`, { method: 'PUT', body: JSON.stringify(patch) });
}

export async function deleteMdmDevice(deviceId: string): Promise<void> {
  await api(`/mdm/sa/devices/${deviceId}`, { method: 'DELETE' });
}

// ── アプリカタログ / サイレント導入 ──────────────────────────────
export interface CatalogApp { id: string; name: string; package_name: string; apk_url: string; note: string; sort_order: number; version_code: number; }
export interface InstallJob { id: string; name: string; package_name: string; status: string; updated_at: string; }

export async function listCatalog(): Promise<CatalogApp[]> {
  const res = await api<{ data: CatalogApp[] }>('/mdm/sa/catalog');
  return res.data;
}
export async function addCatalogApp(input: { name: string; package_name: string; apk_url: string; note?: string; version_code?: number }): Promise<void> {
  await api('/mdm/sa/catalog', { method: 'POST', body: JSON.stringify(input) });
}
export async function deleteCatalogApp(id: string): Promise<void> {
  await api(`/mdm/sa/catalog/${id}`, { method: 'DELETE' });
}
export async function installToDevice(deviceId: string, catalogIds: string[]): Promise<{ queued: number }> {
  const res = await api<{ data: { queued: number } }>(`/mdm/sa/devices/${deviceId}/install`, { method: 'POST', body: JSON.stringify({ catalog_ids: catalogIds }) });
  return res.data;
}
export async function installToAll(catalogIds: string[]): Promise<{ queued: number }> {
  const res = await api<{ data: { queued: number } }>(`/mdm/sa/install-all`, { method: 'POST', body: JSON.stringify({ catalog_ids: catalogIds }) });
  return res.data;
}
export async function listInstallJobs(deviceId: string): Promise<InstallJob[]> {
  const res = await api<{ data: InstallJob[] }>(`/mdm/sa/devices/${deviceId}/install`);
  return res.data;
}

// ── 共有端末トークン（オフライン打刻端末の認証）──────────────────────────
export interface OfflineTerminal {
  id: string; company_id: string; label: string | null; is_active: number;
  created_at: string; last_used_at: string | null;
}
export async function listOfflineTerminals(companyId: string): Promise<OfflineTerminal[]> {
  const res = await api<{ data: OfflineTerminal[] }>(`/superadmin/offline-terminals?company_id=${encodeURIComponent(companyId)}`);
  return res.data;
}
export async function createOfflineTerminal(companyId: string, label: string): Promise<{ id: string; token: string }> {
  const res = await api<{ data: { id: string; token: string } }>(`/superadmin/offline-terminals`, { method: "POST", body: JSON.stringify({ company_id: companyId, label }) });
  return res.data;
}
export async function deleteOfflineTerminal(id: string): Promise<void> {
  await api(`/superadmin/offline-terminals/${id}`, { method: "DELETE" });
}

// ── 体験デモのアクセス元（IPアドレス・地域・回線・端末・操作）────────────────
export interface AccessLogCompany { id: string; name: string; subdomain: string; enabled: boolean; visits: number; }
export interface AccessPlace {
  country: string; country_code: string | null; region: string; city: string;
  visits: number; ips: number; login_visits: number; has_mobile: number; mobile_visits: number;
}
export interface AccessIsp { isp: string; is_mobile: number; visits: number; ips: number; }
export interface AccessSummary {
  places: AccessPlace[]; isps: AccessIsp[];
  totals: { visits: number; ips: number; login_visits: number; ops: number; logins: number; first_at: string | null; last_at: string | null; internal_visits: number };
  internal_breakdown: Array<{ label: string; visits: number }>;
  geo: { month: string | null; fetched_at: string | null; ready: boolean }; geo_pending: number;
  nets: Array<{ net_type: string; visits: number }>;
  domains: Array<{ domain: string; net_type: string; visits: number }>;
  net_labels: Record<string, string>;
}
export interface AccessVisit {
  id: string; ip: string; device: string | null; app: string | null; user_id: string | null; user_name: string | null; employee_code: string | null;
  is_super_admin: number; is_internal: boolean; started_at: string; last_seen_at: string; hits: number; ops: number; logins: number;
  first_path: string | null; user_agent: string | null; country_code: string | null; country: string; region: string | null; city: string | null;
  isp: string | null; asn: number | null; is_mobile: number | null; latitude: number | null; longitude: number | null;
  ptr: string | null; ptr_domain: string | null; host_hint: string | null; net_type: string | null;
  ip_visits: number; ip_first_seen: string | null; ip_users: string | null;
  internal_label: string | null;
}
export interface AccessEvent { id: string; kind: string; label: string; method: string; path: string; status: number; user_name: string | null; detail: string | null; created_at: string; }
export type AccessFilter = Partial<Record<'company_id' | 'from' | 'to' | 'app' | 'country' | 'region' | 'city' | 'isp' | 'mobile' | 'net' | 'domain' | 'ip' | 'user' | 'internal' | 'only_login' | 'include_bots', string>>;
export interface IpRegistry { source: string; fields: Record<string, string>; note?: string; }
/** IPアドレスの登録情報（JPNIC／RDAP）を手動で照会する。結果はサーバーに保存されない。照会は操作ログに残る */
export async function getIpRegistry(ip: string): Promise<IpRegistry> {
  return (await api<{ data: IpRegistry }>(`/superadmin/access-log/ip/${encodeURIComponent(ip)}/registry`)).data;
}

function accessQs(f: AccessFilter & { limit?: number; offset?: number }): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  return q.toString();
}
export async function getAccessCompanies(): Promise<AccessLogCompany[]> {
  return (await api<{ data: AccessLogCompany[] }>(`/superadmin/access-log/companies`)).data;
}
export async function getAccessSummary(f: AccessFilter): Promise<AccessSummary> {
  return (await api<{ data: AccessSummary }>(`/superadmin/access-log/summary?${accessQs(f)}`)).data;
}
export async function getAccessVisits(f: AccessFilter & { limit?: number; offset?: number }): Promise<{ data: AccessVisit[]; total: number }> {
  return api<{ data: AccessVisit[]; total: number }>(`/superadmin/access-log/visits?${accessQs(f)}`);
}
export async function getAccessEvents(visitId: string): Promise<AccessEvent[]> {
  return (await api<{ data: AccessEvent[] }>(`/superadmin/access-log/visits/${encodeURIComponent(visitId)}/events`)).data;
}
export async function exportAccessVisits(f: AccessFilter): Promise<void> {
  const url0 = `${API}/superadmin/access-log/visits.csv?${accessQs(f)}`;
  const h: Record<string, string> = {};
  if (token) h['Authorization'] = `Bearer ${token}`;
  let res = await fetch(url0, { headers: h, credentials: 'include' });
  if (res.status === 401 && await silentRefresh() && token) {
    h['Authorization'] = `Bearer ${token}`;
    res = await fetch(url0, { headers: h, credentials: 'include' });
  }
  if (!res.ok) throw new Error(`CSVの出力に失敗しました（HTTP ${res.status}）`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = `demo-access_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

// ── 当社の回線（社内ネットワーク）の登録 ──────────────────────────────
export interface InternalNetworkEntry { id: string; kind: 'ip' | 'cidr' | 'host'; value: string; label: string; created_by: string | null; created_at: string; resolved: string[]; }
export interface InternalNetworks { registered: InternalNetworkEntry[]; auto: Array<{ ip: string; last_at: string; is_mobile: boolean; isp: string | null }>; }
export async function getInternalNetworks(): Promise<InternalNetworks> {
  return (await api<{ data: InternalNetworks }>(`/superadmin/access-log/internal-networks`)).data;
}
export async function addInternalNetwork(value: string, label: string): Promise<void> {
  await api(`/superadmin/access-log/internal-networks`, { method: 'POST', body: JSON.stringify({ value, label }) });
}
export async function renameInternalNetwork(id: string, label: string): Promise<void> {
  await api(`/superadmin/access-log/internal-networks/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ label }) });
}
export async function deleteInternalNetwork(id: string): Promise<void> {
  await api(`/superadmin/access-log/internal-networks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
